import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link, useHistory, useLocation } from 'react-router-dom'

import { useCurrencyRates } from '@/api/currency/queries/useCurrencyRates'
import { PkgShippingMethod } from '@/api/user-package/types/UserPkg'
import { Divider } from '@/components/Divider'
import { GrowPaymentIframe } from '@/components/GrowPaymentIframe'
import ClockIcon from '@/components/icons/clock.svg?react'
import CostIcon from '@/components/icons/cost.svg?react'
import PackageIcon from '@/components/icons/package.svg?react'
import SuccessIcon from '@/components/icons/success.svg?react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { QUERY_KEYS } from '@/lib/queryKeys'
import { RoutePath } from '@/RoutePath'

import { ShippingOptions } from './ShippingPage'
const shippingMethodLabels = {
  [PkgShippingMethod.DoorToDoor]: 'משלוח עד הבית',
  [PkgShippingMethod.PickUpPoint]: 'איסוף עצמי מנקודת חלוקה',
}
interface Props {
  shippingOptions: ShippingOptions
  paymentUrl: string
  pkgId: string
  totalPrice: number
}

export default function ProcessPage({ shippingOptions, paymentUrl, pkgId, totalPrice }: Props) {
  const history = useHistory()
  const { data: currencyRates } = useCurrencyRates()

  const queryClient = useQueryClient()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const callbackPrice = searchParams.get('callbackPrice')
  const [showSuccessDialog, setShowSuccessDialog] = useState(!!callbackPrice)

  const totalILSPrice = totalPrice * 0.9 * 1.03 * (currencyRates?.poalimRate || 0) + totalPrice * 0.1 * (currencyRates?.BOIrate || 0)

  // Calculate delivery date range (14-21 business days from today)
  const calculateDeliveryDates = () => {
    const today = new Date()
    const addBusinessDays = (date: Date, days: number) => {
      let count = 0
      const result = new Date(date)
      while (count < days) {
        result.setDate(result.getDate() + 1)
        const dayOfWeek = result.getDay()
        // Skip weekends (Saturday=6, Sunday=0)
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          count++
        }
      }
      return result
    }

    const minDate = addBusinessDays(today, 14)
    const maxDate = addBusinessDays(today, 21)

    const formatHebrewDate = (date: Date) => {
      const day = date.getDate()
      const months = [
        'בינואר',
        'בפברואר',
        'במרץ',
        'באפריל',
        'במאי',
        'ביוני',
        'ביולי',
        'באוגוסט',
        'בספטמבר',
        'באוקטובר',
        'בנובמבר',
        'בדצמבר',
      ]
      return `${day} ${months[date.getMonth()]}`
    }

    return `(${formatHebrewDate(minDate)}-${formatHebrewDate(maxDate)})`
  }

  useEffect(() => {
    if (!paymentUrl && !callbackPrice) {
      history.push(`${RoutePath.PaymentSummary}/${pkgId}`)
    }
  }, [history, pkgId, paymentUrl, callbackPrice])

  const onClose = async () => {
    await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USER_PACKAGES] })
    setShowSuccessDialog(false)
    history.push(RoutePath.ClientPkg)
  }

  return (
    <div className='h-[calc(100dvh-var(--navbar-height)-120px)]'>
      {/* Payment Iframe */}
      {!showSuccessDialog && (
        <GrowPaymentIframe
          paymentUrl={paymentUrl}
          onSuccess={() => setShowSuccessDialog(true)}
          onError={() => history.push(`${RoutePath.PaymentSummary}/${pkgId}`)}
          onClose={() => history.push(`${RoutePath.PaymentSummary}/${pkgId}`)}
        />
      )}

      {/* Success Dialog */}
      {showSuccessDialog && (
        <Card>
          <CardContent className='flex flex-col items-center gap-4'>
            {/* Success Icon */}
            <div className='h-6 w-6'>
              <SuccessIcon className='text-icon-primary h-full w-full' />
            </div>

            {/* Title and Description */}
            <div className='flex flex-col items-center gap-4'>
              <h2 className='text-text-primary text-center text-base font-medium'>החבילה בדרך אליך!</h2>
              <p className='text-text-secondary text-center text-sm'>
                איזה כיף!
                <br />
                בקשתך התקבלה, והחבילה תצא אליך בקרוב.
                <br />
                תוכל לעקוב אחריה בטאב ׳חבילות שנשלחו׳
              </p>
            </div>

            {/* Info Container */}
            <div className='bg-button-primary-selected-segment w-full rounded-lg p-4'>
              <div className='flex flex-col gap-2'>
                {/* Cost Info */}
                <div className='flex items-center gap-2'>
                  <CostIcon className='text-text-secondary size-4' />
                  <span className='text-text-secondary text-sm'>עלות משלוח כולל: {callbackPrice ?? totalILSPrice.toFixed(2)} ₪</span>
                </div>

                {/* Package Count */}
                <div className='flex items-center gap-2'>
                  <PackageIcon className='text-text-secondary size-4' />
                  <span className='text-text-secondary text-sm'>
                    סוג המשלוח: {shippingMethodLabels[shippingOptions.selectedShippingMethod]}
                  </span>
                </div>

                {/* Time Info */}
                <div className='flex items-center gap-2'>
                  <ClockIcon className='text-text-secondary size-4' />
                  <span className='text-text-secondary text-sm'>המשלוח צפוי להגיע בתוך 14-21 ימי עסקים {calculateDeliveryDates()}</span>
                </div>
              </div>
            </div>
            <Divider />
            <Link to={RoutePath.ClientPkg}>
              <Button size='sm' variant='ghost' onClick={onClose} className='text-text-link text-sm'>
                חזור לחבילות שלי
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
